<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="anna-mail">
    <div class="anna-mail-actions">
      <oxd-button
        display-type="ghost"
        :label="checking ? 'Checking…' : 'Check mailbox now'"
        :disabled="checking"
        @click="checkNow"
      />
      <oxd-text
        v-if="result"
        tag="p"
        :class="result.ok ? 'anna-mail-ok' : 'anna-mail-err'"
      >
        {{ result.message }}
      </oxd-text>
    </div>
    <oxd-text v-if="emails.length" tag="p" class="anna-mail-status-line">
      Last email handled {{ (emails[0].handledAt || '').slice(0, 16) }}
      <template v-if="count('needs_look')">
        · {{ count('needs_look') }} need a look
      </template>
      <template v-if="count('failed')">
        · {{ count('failed') }} will be tried again
      </template>
    </oxd-text>
    <oxd-text tag="p" class="anna-mail-help">
      Orbit checks the mailbox by itself every minute while it's on.
    </oxd-text>
    <oxd-text v-if="denied" tag="p" class="anna-mail-help">
      Only Admins can see the jobs mailbox.
    </oxd-text>
    <!-- compact (ANNA Settings): the status only; the emails are on the Candidates page -->
    <oxd-text v-if="compact" tag="p" class="anna-mail-help">
      <a :href="candidatesUrl" class="anna-mail-link">
        See every email on the Candidates page (Jobs mailbox)
      </a>
    </oxd-text>
    <table v-else-if="emails.length" class="anna-mail-table">
      <thead>
        <tr>
          <th>Received</th>
          <th>From</th>
          <th>Subject</th>
          <th>Result</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="mail in emails" :key="mail.id">
          <td class="anna-mail-when">
            {{ (mail.receivedAt || '').slice(0, 16) }}
          </td>
          <td>{{ mail.sender }}</td>
          <td>{{ mail.subject }}</td>
          <td>
            <span
              :class="['anna-mail-status', `anna-mail-status--${mail.status}`]"
            >
              {{ labels[mail.status] || mail.status }}
            </span>
            <a
              v-if="mail.candidateId"
              :href="candidateUrl(mail)"
              class="anna-mail-link"
            >
              {{ mail.candidateName || 'the candidate' }}
            </a>
            <template v-if="mail.note">
              <!-- a technical error reads as what it means; the error itself one click away -->
              <details v-if="technical(mail.note)" class="anna-mail-note">
                <summary>{{ plain(mail) }}</summary>
                {{ mail.note }}
              </details>
              <div v-else class="anna-mail-note">{{ mail.note }}</div>
            </template>
          </td>
        </tr>
      </tbody>
    </table>
    <oxd-text v-else-if="loaded && !denied" tag="p" class="anna-mail-help">
      No emails read yet.
    </oxd-text>
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';

// The applications mailbox on ANNA Settings: check now, and what became of each email.
export default {
  name: 'MailboxLog',
  props: {
    // ANNA Settings shows the status only; the Candidates page shows the emails
    compact: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/anna/mailbox',
    );
    // a failed check is shown inline, not as an error toast
    http.setIgnorePath('.*');
    return {http};
  },
  data() {
    return {
      emails: [],
      loaded: false,
      // the mailbox is Admins' (ANNA Settings' data group)
      denied: false,
      candidatesUrl: `${window.appGlobal.baseUrl}/recruitment/viewCandidates`,
      checking: false,
      result: null,
      labels: {
        added: 'Added',
        duplicate: 'Applied again',
        needs_look: 'Needs a look',
        skipped: 'Not an application',
        answers: 'Reply to initial questions',
        reply: 'Candidate wrote',
        failed: 'Will retry',
      },
    };
  },
  created() {
    this.http
      .request({method: 'GET'})
      .then(({data: {data}}) => {
        this.emails = data.recent;
      })
      .catch((e) => {
        this.denied =
          e?.response?.status === 403 || e?.response?.status === 401;
      })
      .finally(() => {
        this.loaded = true;
      });
  },
  methods: {
    count(status) {
      return this.emails.filter((m) => m.status === status).length;
    },
    // an error from Vapi, ANNA or the network: not something HR can act on as written
    technical(note) {
      return /vapi|http|\(\d{3}\)|exception|uuid|curl|timeout|sqlstate/i.test(
        note,
      );
    },
    plain(mail) {
      return mail.status === 'failed'
        ? "Anna couldn't read this email yet: she tries again by herself. Details"
        : "Anna couldn't read this email: please read it yourself. Details";
    },
    checkNow() {
      this.checking = true;
      this.result = null;
      this.http
        .request({method: 'PUT', data: {}})
        .then(({data: {data}}) => {
          this.emails = data.recent;
          const counts = Object.entries(data.counts || {})
            .map(([status, n]) => `${n} ${this.labels[status] || status}`)
            .join(', ');
          this.result = data.enabled
            ? {
                ok: true,
                message: data.busy
                  ? 'Orbit is reading the mailbox right now: its emails show below in a moment.'
                  : data.checked
                  ? `${data.checked} new email(s): ${counts}.`
                  : 'No new emails.',
              }
            : {
                ok: false,
                message:
                  'The mailbox is off: switch on "Read applications from this mailbox" and save.',
              };
        })
        .catch((e) => {
          this.result = {
            ok: false,
            message:
              e?.response?.data?.error?.message ||
              e?.data?.error?.message ||
              'The check failed.',
          };
        })
        .finally(() => {
          this.checking = false;
        });
    },
    candidateUrl(mail) {
      return `${window.appGlobal.baseUrl}/recruitment/addCandidate/${mail.candidateId}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.anna-mail-status-line {
  font-size: 13px;
  font-weight: 600;
  color: #12233f;
  margin-top: 8px;
}
details.anna-mail-note > summary {
  cursor: pointer;
}
.anna-mail-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
.anna-mail-help {
  color: #6a7482;
  font-size: 12px;
  margin: 6px 0 12px;
}
.anna-mail-ok {
  color: #2e7d32;
  font-size: 13px;
}
.anna-mail-err {
  color: #c62828;
  font-size: 13px;
}
.anna-mail-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th {
    text-align: left;
    color: #64728c;
    font-weight: 600;
    padding: 6px 8px;
    border-bottom: 1px solid #e7ebf1;
  }
  td {
    padding: 8px;
    border-bottom: 1px solid #f2f4f7;
    vertical-align: top;
  }
}
.anna-mail-when {
  white-space: nowrap;
  color: #64728c;
}
.anna-mail-status {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  background: #eef1f5;
  color: #64728c;
  &--added,
  &--answers,
  &--reply {
    background: #e3f3e6;
    color: #2e7d32;
  }
  &--needs_look,
  &--failed {
    background: #fff3d6;
    color: #a05a00;
  }
}
.anna-mail-link {
  margin-left: 8px;
  color: var(--oxd-primary-one-color);
}
.anna-mail-note {
  margin-top: 4px;
  color: #6a7482;
  font-size: 12px;
}
</style>
